import {PencilIcon, PlusIcon, TrashIcon} from '@heroicons/react/solid';

import {Button, Select} from '@/components';
import {NumberInput} from '@/components/common/Input';
import type {AfriexCorridorCost, AfriexTransactionProcessor} from '@/types';
import {
  AfriexCountryCode,
  AfriexCurrencyCode,
  AfriexTransactionChannel,
} from '@/types';

import useProcessorCosts from './useProcessorCosts';

const countryOptions = Object.values(AfriexCountryCode).map((c) => ({
  key: c,
  value: c,
}));
const currencyOptions = Object.values(AfriexCurrencyCode).map((c) => ({
  key: c,
  value: c,
}));
const channelOptions = Object.values(AfriexTransactionChannel).map((ch) => ({
  key: ch,
  value: ch,
}));
const amountTypeOptions = [
  {key: '%', value: 'PERCENTAGE' as const},
  {key: '#', value: 'COUNT' as const},
];

type Props = {
  processorCode?: AfriexTransactionProcessor;
  costs: AfriexCorridorCost[];
};

const ProcessorCostsTab = ({processorCode, costs: initialCosts}: Props) => {
  const {
    costs,
    isLoading,
    isEditing,
    editingId,
    editForm,
    startAdd,
    startEdit,
    cancelEdit,
    updateFormField,
    updateAmountField,
    handleSave,
    handleDelete,
  } = useProcessorCosts(processorCode, initialCosts);
  if (!processorCode) {
    return (
      <p className="text-sm text-slate-400 italic py-4">
        Save the processor first before managing costs.
      </p>
    );
  }

  const editingRow = editingId
    ? costFormRow(
        editForm,
        {
          updateFormField,
          updateAmountField,
          handleSave,
          cancelEdit,
        },
        isEditing,
      )
    : newCostRow(
        editForm,
        {
          handleSave,
          cancelEdit,
          updateFormField,
          updateAmountField,
        },
        isEditing,
      );

  return (
    <div className="py-2">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">
          Processor Costs
        </h3>
        {!isEditing && (
          <Button
            variant="solid"
            colorScheme="cyan"
            size="sm"
            onClick={startAdd}
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            Add Cost
          </Button>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-400">Loading costs...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-800 text-left">
                <th className="p-2 font-medium">Country</th>
                <th className="p-2 font-medium">Currency</th>
                <th className="p-2 font-medium">Channel</th>
                <th className="p-2 font-medium">Type</th>
                <th className="p-2 font-medium">Value</th>
                {!isEditing && <th className="p-2 font-medium">Active</th>}
                <th className="p-2 font-medium w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isEditing ? (
                editingRow
              ) : (
                <>
                  {costs.map((cost) =>
                    displayRow(cost, {startEdit, handleDelete}),
                  )}
                  {!costs.length && (
                    <tr>
                      <td
                        colSpan={7}
                        className="p-4 text-center text-slate-400"
                      >
                        No costs configured
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Sourced from the hook rather than re-declared, the same way `FormActions`
// below already is: `handleDelete` is async (it awaits the cost update before
// dispatching), and the hand-written `=> void` hid that from the call site.
type DisplayActions = Pick<
  ReturnType<typeof useProcessorCosts>,
  'startEdit' | 'handleDelete'
>;

type FormActions = Pick<
  ReturnType<typeof useProcessorCosts>,
  'handleSave' | 'cancelEdit' | 'updateFormField' | 'updateAmountField'
>;

const displayRow = (cost: AfriexCorridorCost, actions: DisplayActions) => (
  <tr key={cost.id} className="border-t border-gray-200 dark:border-gray-700">
    <td className="p-2">{cost.countryCode}</td>
    <td className="p-2">{cost.currencyCode}</td>
    <td className="p-2">{cost.channel}</td>
    <td className="p-2">{cost.amount?.type}</td>
    <td className="p-2">{cost.amount?.value}</td>
    <td className="p-2">
      <span
        className={`px-1.5 py-0.5 rounded text-xs ${
          cost.isActive
            ? 'bg-green-100 text-green-700'
            : 'bg-gray-100 text-gray-500'
        }`}
      >
        {cost.isActive ? 'Yes' : 'No'}
      </span>
    </td>
    <td className="p-2">
      <div className="flex gap-1">
        <Button
          variant="unStyled"
          size="sm"
          onClick={() => actions.startEdit(cost)}
        >
          <PencilIcon className="w-4 h-4 text-blue-500" />
        </Button>
        <Button
          variant="unStyled"
          size="sm"
          onClick={() => actions.handleDelete(cost.id)}
        >
          <TrashIcon className="w-4 h-4 text-red-500" />
        </Button>
      </div>
    </td>
  </tr>
);

const newCostRow = (
  form: Partial<AfriexCorridorCost>,
  actions: FormActions,
  isEditing: boolean,
) => costFormRow(form, actions, isEditing);

const costFormRow = (
  form: Partial<AfriexCorridorCost>,
  actions: FormActions,
  isEditing: boolean,
) => (
  <tr
    key="edit-row"
    className="border-t border-gray-200 dark:border-gray-700 bg-cyan-50 dark:bg-cyan-900/20"
  >
    <td className="py-2">
      <Select
        id="cost-country"
        name="cost-country"
        value={String(form.countryCode || '')}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          actions.updateFormField('countryCode', e.target.value)
        }
        options={countryOptions}
        placeholder="Country"
        className="min-w-[100px]"
        bodyClassName="!text-xs"
      />
    </td>
    <td className="p-2">
      <Select
        id="cost-currency"
        name="cost-currency"
        value={String(form.currencyCode || '')}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          actions.updateFormField('currencyCode', e.target.value)
        }
        options={currencyOptions}
        placeholder="Currency"
        className="min-w-[110px]"
        bodyClassName="!text-xs"
      />
    </td>
    <td className="p-2">
      <Select
        id="cost-channel"
        name="cost-channel"
        value={String(form.channel || '')}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          actions.updateFormField('channel', e.target.value)
        }
        options={channelOptions}
        placeholder="Channel"
        className="min-w-[140px]"
        bodyClassName="!text-xs"
      />
    </td>
    <td className="p-2">
      <Select
        id="cost-type"
        name="cost-type"
        value={String(form.amount?.type || 'PERCENTAGE')}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          actions.updateAmountField('type', e.target.value)
        }
        options={amountTypeOptions}
        className="min-w-[80px]"
        bodyClassName="!text-xs"
      />
    </td>
    <td className="p-2">
      <NumberInput
        min={0}
        value={form.amount?.value ?? 0}
        onValueChange={(v: string) =>
          actions.updateAmountField('value', Number(v) || 0)
        }
        className="w-[100px] !text-xs"
      />
    </td>
    {!isEditing && <td className="p-2" />}
    <td className="p-2">
      <div className="flex gap-1">
        <Button
          variant="solid"
          colorScheme="cyan"
          className="!py-1 !px-2 !text-xs"
          onClick={() => actions.handleSave()}
        >
          Save
        </Button>
        <Button
          variant="outline"
          colorScheme="indigo"
          className="!py-1 !px-2 !text-xs"
          onClick={actions.cancelEdit}
        >
          Cancel
        </Button>
      </div>
    </td>
  </tr>
);

export default ProcessorCostsTab;
